<template>
  <div class="places-page">
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-lg-8 col-md-10">

          <!-- Header Section -->
          <div class="page-header text-center mb-5">
            <h1 class="display-4 mb-3">Places on SocialHood</h1>
            <p class="lead text-muted">
              We've evolved our approach to community connections
            </p>
          </div>

          <!-- Main Content -->
          <div class="content-section mb-5">
            <div class="alert alert-info" role="alert">
              <h4 class="alert-heading">
                <i class="fas fa-info-circle me-2"></i>
                Service Update
              </h4>
              <p class="mb-0">
                SocialHood no longer features content organized by specific places or locations.
                We've shifted our focus to neighborhood-based community discussions that bring
                neighbors together in meaningful ways.
              </p>
            </div>

            <h3 class="mb-3">What's Changed?</h3>
            <p>
              Previously, SocialHood organized content around specific places and venues.
              We've learned that the most valuable community connections happen when neighbors
              engage with each other directly, regardless of specific locations.
            </p>

            <h3 class="mb-3">How to Connect Now</h3>
            <div class="row">
              <div class="col-md-6 mb-3">
                <div class="card h-100">
                  <div class="card-body">
                    <h5 class="card-title">
                      <i class="fas fa-home me-2 text-primary"></i>
                      Find Your Neighborhood
                    </h5>
                    <p class="card-text">
                      Search by zipcode or neighborhood name to connect with your immediate community.
                    </p>
                    <a href="/neighborhoods" class="btn btn-primary">Browse Neighborhoods</a>
                  </div>
                </div>
              </div>
              <div class="col-md-6 mb-3">
                <div class="card h-100">
                  <div class="card-body">
                    <h5 class="card-title">
                      <i class="fas fa-users me-2 text-success"></i>
                      Join Discussions
                    </h5>
                    <p class="card-text">
                      Participate in neighborhood conversations, share local insights, and build relationships.
                    </p>
                    <a href="/" class="btn btn-success">Start Connecting</a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- FAQ Section -->
          <div class="faq-section">
            <h3 class="mb-4">Frequently Asked Questions</h3>

            <div class="accordion" id="placesAccordion">
              <div class="accordion-item">
                <h2 class="accordion-header" id="headingOne">
                  <button class="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#collapseOne">
                    Why did SocialHood stop featuring places?
                  </button>
                </h2>
                <div id="collapseOne" class="accordion-collapse collapse show" data-bs-parent="#placesAccordion">
                  <div class="accordion-body">
                    We found that focusing on neighborhood communities rather than specific venues
                    creates stronger, more lasting connections between neighbors. This approach
                    emphasizes relationships over locations.
                  </div>
                </div>
              </div>

              <div class="accordion-item">
                <h2 class="accordion-header" id="headingTwo">
                  <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseTwo">
                    Can I still discuss local businesses and venues?
                  </button>
                </h2>
                <div id="collapseTwo" class="accordion-collapse collapse" data-bs-parent="#placesAccordion">
                  <div class="accordion-body">
                    Absolutely! You can discuss local businesses, restaurants, and venues within
                    your neighborhood community. This creates more contextual and relevant
                    conversations for your immediate area.
                  </div>
                </div>
              </div>

              <div class="accordion-item">
                <h2 class="accordion-header" id="headingThree">
                  <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseThree">
                    How do I find my old places content?
                  </button>
                </h2>
                <div id="collapseThree" class="accordion-collapse collapse" data-bs-parent="#placesAccordion">
                  <div class="accordion-body">
                    Previous places content has been archived. For similar discussions,
                    check your neighborhood community where locals often share recommendations
                    and experiences about nearby venues.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Call to Action -->
          <div class="cta-section text-center mt-5 p-4 bg-light rounded">
            <h4 class="mb-3">Ready to Connect with Your Neighbors?</h4>
            <p class="mb-3">
              Discover what's happening in your neighborhood and join the conversation.
            </p>
            <div class="d-flex justify-content-center gap-3 flex-wrap">
              <a href="/" class="btn btn-primary btn-lg">
                <i class="fas fa-search me-2"></i>
                Find My Neighborhood
              </a>
              <a href="/about" class="btn btn-outline-secondary btn-lg">
                <i class="fas fa-info-circle me-2"></i>
                Learn More
              </a>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useHead } from '@vueuse/head'

export default {
  name: 'PlacesPage',
  setup() {
    useHead({
      title: 'Places - SocialHood',
      meta: [
        {
          name: 'description',
          content: 'SocialHood has evolved from place-based content to neighborhood community discussions. Find your neighborhood and connect with neighbors.'
        },
        {
          property: 'og:title',
          content: 'Places - SocialHood'
        },
        {
          property: 'og:description',
          content: 'SocialHood has evolved from place-based content to neighborhood community discussions. Find your neighborhood and connect with neighbors.'
        },
        {
          property: 'og:type',
          content: 'website'
        }
      ]
    })
  }
}
</script>

<style scoped>
.places-page {
  padding: 2rem 0;
  min-height: 70vh;
}

.page-header {
  margin-bottom: 3rem;
}

.content-section {
  margin-bottom: 3rem;
}

.card {
  border: none;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  transition: transform 0.2s ease;
}

.card:hover {
  transform: translateY(-2px);
}

.accordion-button {
  font-weight: 500;
}

.cta-section {
  border: 2px solid #e9ecef;
}

.btn-lg {
  padding: 0.75rem 1.5rem;
  font-size: 1.1rem;
}

@media (max-width: 768px) {
  .places-page {
    padding: 1rem 0;
  }

  .display-4 {
    font-size: 2.5rem;
  }

  .d-flex.gap-3 {
    flex-direction: column;
    gap: 1rem !important;
  }

  .btn-lg {
    width: 100%;
  }
}
</style>